import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  Alert,
  IconButton,
  MenuItem,
  CircularProgress,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { format, parseISO } from 'date-fns'
import { useNavigate } from 'react-router-dom'

import { submitB2CBooking } from '../services/api'
import config from '../config'

const POLICY_URL = process.env.REACT_APP_POLICY_URL || 'https://media.mygroupbooker.com/policy'

// Accepts international (+1, +44, etc.) and local (0...) phone numbers, 7-15 digits
const PHONE_REGEX = /^\+?\d[\d\s\-()]{5,18}\d$/

const BookNowDialog = ({
  open,
  onClose,
  booking,
  company,
  jobTypes,
  searchJobTypeId,
  searchPostcode,
}) => {
  const navigate = useNavigate()

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    postcode: searchPostcode || '',
    additionalInfo: '',
    jobTypeId: searchJobTypeId || '',
  })
  const [agreeToTerms, setAgreeToTerms] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [fieldErrors, setFieldErrors] = useState({})

  const handleChange = e => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: null }))
    }
  }

  const validate = () => {
    const errors = {}
    if (!formData.firstName.trim()) errors.firstName = 'Name is required'
    if (!formData.email.trim()) errors.email = 'Email is required'
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      errors.email = 'Enter a valid email address'
    if (formData.phone.trim() && !PHONE_REGEX.test(formData.phone.trim()))
      errors.phone = 'Enter a valid phone number (e.g. 07911 123456 or +1 555 123 4567)'
    if (!formData.address.trim()) errors.address = 'Address is required'
    if (!formData.postcode.trim()) errors.postcode = 'Postcode is required'
    if (!selectedJobTypeId) errors.jobTypeId = 'Please select a service'
    if (!agreeToTerms) errors.terms = 'You must agree to the terms and conditions'
    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  const selectedJobTypeId = formData.jobTypeId || searchJobTypeId
  const selectedJobType = jobTypes?.find(jt => jt.id === selectedJobTypeId)
  const currentPrice = booking?.currentPrices?.[selectedJobTypeId]

  const formatDate = dateStr => {
    try {
      return format(parseISO(dateStr), 'EEEE do MMMM')
    } catch {
      return dateStr
    }
  }

  const formatTime = timeStr => {
    if (!timeStr) return ''
    const parts = timeStr.split(':')
    return `${parts[0]}:${parts[1]}`
  }

  const handleSubmit = async e => {
    e.preventDefault()
    if (!validate()) return

    setSubmitting(true)
    setError(null)

    try {
      const slug = company?.slug || config.companySlug
      await submitB2CBooking({
        company_slug: slug,
        group_booking_id: booking.id,
        job_type_id: selectedJobTypeId,
        customer_email: formData.email.trim(),
        customer_first_name: formData.firstName.trim(),
        customer_last_name: formData.lastName.trim(),
        customer_phone: formData.phone.trim(),
        customer_address: formData.address.trim(),
        customer_postcode: formData.postcode.trim().toUpperCase(),
        additional_information: formData.additionalInfo.trim(),
      })

      onClose()
      navigate('/booking-confirmation', {
        state: {
          booking: {
            date: booking.date,
            startTime: booking.startTime,
            endTime: booking.endTime,
            currentPrice: currentPrice != null ? currentPrice.toFixed(2) : null,
            chargeVat: company?.chargeVat,
          },
          jobTypeName: selectedJobType?.name,
          companyName: company?.name,
          companySlug: slug,
        },
      })
    } catch (err) {
      const data = err.response?.data
      const detail = data?.detail
      if (err.response?.status === 409) {
        setError('This booking is full. Please choose another date.')
      } else if (detail) {
        setError(detail)
      } else if (data && typeof data === 'object') {
        // Handle DRF field-level validation errors (e.g. {"field": ["error"]})
        const messages = Object.values(data).flat().filter(m => typeof m === 'string')
        setError(messages.join(' ') || 'Something went wrong. Please try again.')
      } else {
        setError('Something went wrong. Please try again.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const showJobTypeSelector = !searchJobTypeId && jobTypes?.length > 1

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          pb: 1,
        }}
      >
        <Box>
          <Typography variant='h6' component='div'>
            {selectedJobType?.name || 'Book Now'}
          </Typography>
          {booking && (
            <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
              {formatDate(booking.date)}
              {booking.startTime && booking.endTime && (
                <>
                  {' '}
                  &middot; Arrival time: {formatTime(booking.startTime)}-
                  {formatTime(booking.endTime)}
                </>
              )}
              {currentPrice != null && (
                <>
                  <br />
                  Book now for: £
                  {currentPrice.toFixed(2)}{company?.chargeVat ? ' (inc VAT)' : ''}
                </>
              )}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size='small' sx={{ mt: -0.5, mr: -1 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers sx={{ pt: 2 }}>
          {error && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {showJobTypeSelector && (
            <TextField
              select
              fullWidth
              label='Service type'
              name='jobTypeId'
              value={formData.jobTypeId}
              onChange={handleChange}
              error={!!fieldErrors.jobTypeId}
              helperText={fieldErrors.jobTypeId}
              sx={{ mb: 2 }}
              required
            >
              {jobTypes.map(jt => (
                <MenuItem key={jt.id} value={jt.id}>
                  {jt.name}
                </MenuItem>
              ))}
            </TextField>
          )}

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              label='Name'
              name='firstName'
              value={formData.firstName}
              onChange={handleChange}
              error={!!fieldErrors.firstName}
              helperText={fieldErrors.firstName}
              required
            />
            <TextField
              fullWidth
              label='Surname'
              name='lastName'
              value={formData.lastName}
              onChange={handleChange}
            />
          </Box>

          <TextField
            fullWidth
            label='Email'
            name='email'
            type='email'
            value={formData.email}
            onChange={handleChange}
            error={!!fieldErrors.email}
            helperText={fieldErrors.email}
            sx={{ mb: 2 }}
            required
          />

          <TextField
            fullWidth
            label='Contact number'
            name='phone'
            type='tel'
            value={formData.phone}
            onChange={handleChange}
            error={!!fieldErrors.phone}
            helperText={fieldErrors.phone}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label='Address'
            name='address'
            value={formData.address}
            onChange={handleChange}
            error={!!fieldErrors.address}
            helperText={fieldErrors.address}
            sx={{ mb: 2 }}
            required
          />

          <TextField
            fullWidth
            label='Postcode'
            name='postcode'
            value={formData.postcode}
            onChange={handleChange}
            error={!!fieldErrors.postcode}
            helperText={fieldErrors.postcode}
            sx={{ mb: 2 }}
            required
          />

          <TextField
            fullWidth
            label='Additional information you would like to tell us'
            name='additionalInfo'
            value={formData.additionalInfo}
            onChange={handleChange}
            multiline
            rows={3}
            sx={{ mb: 2 }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={agreeToTerms}
                onChange={e => {
                  setAgreeToTerms(e.target.checked)
                  if (fieldErrors.terms) {
                    setFieldErrors(prev => ({ ...prev, terms: null }))
                  }
                }}
              />
            }
            label={
              <Typography variant='body2'>
                I agree with the{' '}
                <a
                  href={`${POLICY_URL}/terms-and-conditions.html`}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  T&amp;C's
                </a>{' '}
                and{' '}
                <a
                  href={`${POLICY_URL}/privacy-policy.html`}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  Privacy Policy
                </a>{' '}
                of this website.
              </Typography>
            }
          />
          {fieldErrors.terms && (
            <Typography variant='caption' color='error' sx={{ ml: 2 }}>
              {fieldErrors.terms}
            </Typography>
          )}

          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ mt: 2, fontStyle: 'italic' }}
          >
            When more people join, the price drops &ndash; the more bookings on
            the day, the less you pay.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type='submit'
            variant='contained'
            disabled={submitting}
            sx={{
              bgcolor: 'secondary.main',
              color: 'primary.main',
              '&:hover': { bgcolor: 'secondary.dark' },
            }}
          >
            {submitting ? <CircularProgress size={24} /> : 'Book now'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default BookNowDialog
